import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "../../lib/api";
import StatCard from "../../components/common/StatCard";
import { AlertTriangle, Inbox, ArrowRight } from "lucide-react";
import EnableNotificationsPrompt from "../../components/common/EnableNotificationsPrompt";
import PushDiagnostics from "../../components/admin/PushDiagnostics";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/admin/dashboard")
      .then(({ data }) => alive && setData(data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Failed to load"));
    api.get("/admin/inbox")
      .then(({ data }) => {
        if (!alive) return;
        // Defensive: every list under recent.* must be an array — a stale
        // SW or partial response could otherwise blow up .map/.length.
        const safe = {
          counts: {
            products: data?.counts?.products || 0,
            sellers: data?.counts?.sellers || 0,
            returns: data?.counts?.returns || 0,
            total: data?.counts?.total || 0,
          },
          recent: {
            products: Array.isArray(data?.recent?.products) ? data.recent.products : [],
            sellers: Array.isArray(data?.recent?.sellers) ? data.recent.sellers : [],
            returns: Array.isArray(data?.recent?.returns) ? data.recent.returns : [],
          },
        };
        setInbox(safe);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data && !error) {
    return (
      <div data-testid="admin-dashboard-page">
        <div className="overline mb-2">Overview</div>
        <h1 className="font-display text-4xl mb-8">Platform Pulse</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-28 bg-white border border-[hsl(var(--kb-border))] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="admin-error" className="border border-red-300 bg-red-50 text-red-800 p-4 text-sm">
        {error}
      </div>
    );
  }

  const c = data.counts || {};
  const salesTrend = Array.isArray(data.sales_trend) ? data.sales_trend : [];
  const max = Math.max(...salesTrend.map((d) => d.total), 1);

  return (
    <div data-testid="admin-dashboard-page">
      <div className="overline mb-2">Overview</div>
      <h1 className="font-display text-4xl mb-8">Platform Pulse</h1>

      <div className="mb-6">
        <EnableNotificationsPrompt
          storageKey="admin_orders"
          title="Get notified when orders arrive"
          message="We'll send a browser notification the moment a customer places an order — even if you've closed this tab. Required for time-sensitive fulfilment."
          ctaLabel="Enable order alerts"
          variant="banner"
          testIdPrefix="admin-orders-push"
        />
      </div>

      <div className="mb-6">
        <PushDiagnostics />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="GMV (Paid)" value={money(data.gmv)} testid="adm-stat-gmv" />
        <StatCard label="Platform Fee" value={money(data.platform_fee)} testid="adm-stat-fee" />
        <StatCard label="Orders (Paid)" value={c.orders_paid} testid="adm-stat-paid" />
        <StatCard label="Customers" value={c.customers} testid="adm-stat-customers" />
        <StatCard label="Sellers" value={`${c.sellers_approved}/${c.sellers_total}`} testid="adm-stat-sellers" />
        <StatCard label="Pending Sellers" value={c.sellers_pending} testid="adm-stat-pending-sellers" />
        <StatCard label="Products Pending" value={c.products_pending} testid="adm-stat-pending-prod" />
        <StatCard label="Products Active" value={c.products_active} testid="adm-stat-active-prod" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[hsl(var(--kb-border))] p-6">
          <div className="overline mb-4">Sales · last 7 days</div>
          <div className="flex items-end gap-2 h-44" data-testid="sales-trend-chart">
            {salesTrend.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-[hsl(var(--kb-primary))]/80 hover:bg-[hsl(var(--kb-primary))] transition-colors"
                  style={{ height: `${(d.total / max) * 100}%` }}
                  title={`${d.date}: ${money(d.total)}`}
                />
                <div className="text-[10px] text-[hsl(var(--kb-muted))]">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="low-stock-panel">
          <div className="flex items-center gap-2 overline mb-4">
            <AlertTriangle size={14} /> Low stock alerts
          </div>
          {data.low_stock.length === 0 ? (
            <div className="text-sm text-[hsl(var(--kb-muted))]">All products healthy.</div>
          ) : (
            <div className="space-y-3 text-sm">
              {data.low_stock.map((p) => (
                <div key={p.product_id} className="flex justify-between gap-2">
                  <div className="truncate">{p.title}</div>
                  <div className="text-[hsl(var(--kb-primary))] font-medium shrink-0">{p.total_stock} left</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {inbox && inbox.counts.total > 0 && (
        <div className="mt-8 bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="admin-inbox-panel">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 overline">
              <Inbox size={14} /> Moderation Inbox · {inbox.counts.total} pending
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div data-testid="inbox-products">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium">Products · {inbox.counts.products}</div>
                {inbox.counts.products > 0 && (
                  <Link to="/admin/products?approval=pending" className="text-xs text-[hsl(var(--kb-primary))] flex items-center gap-1">
                    Review <ArrowRight size={12} />
                  </Link>
                )}
              </div>
              {inbox.recent.products.length === 0 ? (
                <div className="text-xs text-[hsl(var(--kb-muted))]">No pending products.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {inbox.recent.products.map((p) => (
                    <li key={p.id} className="truncate text-[hsl(var(--kb-muted))]">{p.title}</li>
                  ))}
                </ul>
              )}
            </div>
            <div data-testid="inbox-sellers">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium">Sellers · {inbox.counts.sellers}</div>
                {inbox.counts.sellers > 0 && (
                  <Link to="/admin/sellers?status=pending" className="text-xs text-[hsl(var(--kb-primary))] flex items-center gap-1">
                    Review <ArrowRight size={12} />
                  </Link>
                )}
              </div>
              {inbox.recent.sellers.length === 0 ? (
                <div className="text-xs text-[hsl(var(--kb-muted))]">No pending sellers.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {inbox.recent.sellers.map((s) => (
                    <li key={s.id} className="truncate text-[hsl(var(--kb-muted))]">{s.shop_name}</li>
                  ))}
                </ul>
              )}
            </div>
            <div data-testid="inbox-returns">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium">Returns · {inbox.counts.returns}</div>
                {inbox.counts.returns > 0 && (
                  <Link to="/admin/returns" className="text-xs text-[hsl(var(--kb-primary))] flex items-center gap-1">
                    Review <ArrowRight size={12} />
                  </Link>
                )}
              </div>
              {inbox.recent.returns.length === 0 ? (
                <div className="text-xs text-[hsl(var(--kb-muted))]">No pending returns.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {inbox.recent.returns.map((r) => (
                    <li key={r.id} className="truncate text-[hsl(var(--kb-muted))]">{r.reason || "Return request"}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
